import {
  ChevronDownIcon,
  Database02Icon,
  Edit03Icon,
  InfoCircleIcon,
  PlusIcon,
} from '@langchain/untitled-ui-icons';
import {
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  Input as MuiInput,
  Radio,
  Slider,
  Switch,
} from '@mui/joy';

import { omit } from 'lodash-es';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import {
  Controller,
  FieldError,
  useController,
  useForm,
} from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { validateBackfillDate } from '@/Pages/Settings/utils/helpers';
import { AddToAnnotationQueuePopoverContent } from '@/components/AnnotationQueuePopover/AddToAnnotationQueuePopoverContent';
import { AnnotationQueueCrudPane } from '@/components/AnnotationQueuePopover/AnnotationQueueCrudPane';
import { DatasetCrudPane } from '@/components/DatasetCrudPane/DatasetCrudPane';
import { DocsButton } from '@/components/DocsButton';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { FilterBar } from '@/components/FilterBar/FilterBar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { RulesWebhookPane } from '@/components/Rules/RulesWebhookPane';
import { DatasetSelectorPopoverContent } from '@/components/RunsTable/components/DatasetSelectorPopoverContent';
import { useDebouncedFilter } from '@/components/RunsTable/hooks/useDebouncedFilter';
import { useSearchModel } from '@/components/RunsTable/hooks/useSearchModel';
import { Button, Input, Text, Tooltip } from '@/design-system';
import {
  GridTimeModel,
  tryJsonParseSearchParam,
} from '@/hooks/useDataGridState';
import {
  InstanceFlags,
  useInstanceFlagOrDefault,
} from '@/hooks/useInstanceFlag';
import {
  useAnnotationQueues,
  useDataset,
  useRunRulesMutation,
  useRunStats,
} from '@/hooks/useSwr';
import {
  GetRunsQueryParams,
  RuleMutateBody,
  RuleSchema,
  SessionRuleMutateBody,
} from '@/types/schema';
import { pluralize } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { SearchModel } from '../RunsTable/types';
import {
  DEFAULT_FILTER_STRING,
  useTabAwareFilters,
} from './hooks/useTabAwareFilters';

export const BackfillFromFormLabel = (props: { disabled: boolean }) => (
  <div className="flex flex-row gap-2">
    <Text variant="md" weight="medium">
      Backfill from
    </Text>
    {props.disabled && (
      <Tooltip title={'Setting backfill is only allowed on rule creation'}>
        <InfoCircleIcon className="my-auto ml-1 h-4 w-4" />
      </Tooltip>
    )}
  </div>
);

export function BackfillDateInput(props: {
  value: string | undefined;
  onChange: (value: string | null) => void;
  disabled: boolean;
  error?: FieldError;
}) {
  return (
    <>
      <MuiInput
        type="date"
        value={props.value ? props.value.substring(0, 10) : ''}
        onChange={(e) => {
          const selectedDate = new Date(e.target.value);
          if (!isNaN(selectedDate.getTime())) {
            props.onChange(selectedDate.toISOString());
          } else {
            props.onChange(null);
          }
        }}
        disabled={props.disabled}
        size="sm"
      />
      <FormHelperText className="empty:hidden">
        {props.error?.message}
      </FormHelperText>
    </>
  );
}

export function RulesFilterInput(props: {
  sessionId?: string | undefined;
  value: SearchModel;
  onChange: (value: SearchModel) => void;
  statsFilter?: GetRunsQueryParams;
  className?: string;
}) {
  // Unify this filter's search param with run tables.
  const [searchParams] = useSearchParams();
  const [timeModel] = useState<GridTimeModel>(() =>
    tryJsonParseSearchParam(searchParams, 'timeModel', {} as GridTimeModel)
  );
  const { searchModel } = useSearchModel();

  const runsSessionFilter = useMemo(
    () => ({ session: [props.sessionId ?? ''] }),
    [props.sessionId]
  );
  const debouncedRunsFilter = useDebouncedFilter({
    timeModel,
    runsFilter: runsSessionFilter,
    searchModel,
  });

  const stats = useRunStats(
    debouncedRunsFilter ? omit(debouncedRunsFilter, 'filter') : null,
    undefined,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    }
  );

  return (
    <FilterBar
      variant="form"
      stats={stats.data}
      value={props.value}
      onChange={props.onChange}
      className={props.className}
    />
  );
}

function RulesAnnotationQueueInput(props: {
  value: string | undefined;
  onChange: (value: string) => void;
}) {
  const [paneOpen, setPaneOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const queue = useAnnotationQueues(
    props.value ? { ids: [props.value] } : null
  );
  const label = queue.data?.find((i) => i.id === props.value)?.name;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="normal"
            color="secondary"
            className="w-fit max-w-full"
          >
            <Edit03Icon className="h-4 w-4 shrink-0" />
            <Text className="truncate">{label == null ? 'Select' : label}</Text>
            <ChevronDownIcon
              className={cn('h-4 w-4 transition-all', open && 'rotate-180')}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-[50vh] min-w-[375px] overflow-auto p-0"
        >
          <AddToAnnotationQueuePopoverContent
            handleSendToAnnotationQueue={(aq) => {
              props.onChange(aq.id);
              setOpen(false);
            }}
            openNewAnnotationQueueModal={() => setPaneOpen(true)}
          />
        </PopoverContent>
      </Popover>
      <AnnotationQueueCrudPane
        onSuccess={(queue) => props.onChange(queue.id)}
        open={paneOpen}
        closeModal={() => setPaneOpen(false)}
      />
    </>
  );
}

function RulesDatasetInput(props: {
  value: string | undefined;
  onChange: (value: string) => void;
  error?: string;
}) {
  const [paneOpen, setPaneOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const dataset = useDataset(props.value);
  const label = dataset.data?.name;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="normal"
            color="secondary"
            className="w-fit max-w-full"
          >
            <Database02Icon className="h-4 w-4 shrink-0" />
            <Text className="truncate">{label == null ? 'Select' : label}</Text>
            <ChevronDownIcon
              className={cn('h-4 w-4 transition-all', open && 'rotate-180')}
            />
          </Button>
        </PopoverTrigger>

        {props.error && (
          <span className="mt-1 text-sm text-error">{props.error}</span>
        )}

        <PopoverContent
          align="start"
          className="max-h-[50vh] min-w-[375px] overflow-auto p-0"
        >
          <DatasetSelectorPopoverContent
            handleSelectDataset={(dataset) => {
              setOpen(false);
              props.onChange(dataset.id);
            }}
            openNewDatasetPane={() => setPaneOpen(true)}
            className="h-[400px]"
          />
        </PopoverContent>
      </Popover>
      <DatasetCrudPane
        onSuccess={(id) => {
          setPaneOpen(false);
          props.onChange(id);
        }}
        onCancel={() => setPaneOpen(false)}
        label={'New dataset'}
        open={paneOpen}
      />
    </>
  );
}

function RulesActionSwitch(props: {
  active: boolean;
  revealWhenInactive?: boolean;
  label: ReactNode;
  children?: ReactNode;
  onClick: () => void;
  docsLink: string;
}) {
  return (
    <Button
      color="secondary"
      variant="outlined"
      className="flex flex-col items-start p-3"
      onClick={props.onClick}
    >
      <div className="flex items-center gap-3">
        <Radio checked={props.active} onChange={props.onClick} size="sm" />
        <Text variant="md" className="text-black dark:text-white">
          {props.label}
        </Text>
        <DocsButton link={props.docsLink} className="-ml-1" />
      </div>

      {props.children && (props.active || props.revealWhenInactive) && (
        <div className="flex flex-col py-1 pl-7 pr-4 pt-2">
          {props.children}
        </div>
      )}
    </Button>
  );
}

export function SessionRulesCrudFormV2(props: {
  id?: string;
  sessionId: string;
  initialData?: RuleSchema;
  onSuccess: () => void;
  runTableFilters?: SearchModel;
  setRunTableFilters?: (filters: SearchModel) => void;
  onClose: () => void;
}) {
  'use no memo';
  const [applyToPastRuns, setApplyToPastRuns] = useState(false);

  const form = useForm<SessionRuleMutateBody>({
    defaultValues: {
      ...props.initialData,
      sampling_rate: props.initialData?.sampling_rate ?? 1,
      is_enabled: props.initialData?.is_enabled ?? true,
    },
  });

  // Add validation for ensuring at least one action is selected
  const validateAtLeastOneAction = () => {
    const values = form.getValues();
    const hasAction =
      !!values.add_to_annotation_queue_id ||
      !!values.add_to_dataset_id ||
      (values.webhooks && values.webhooks.length > 0) ||
      values.extend_only;

    return hasAction || 'At least one action must be selected';
  };

  // Add a hidden field to trigger validation
  useController({
    name: 'add_to_annotation_queue_id',
    control: form.control,
    rules: { validate: validateAtLeastOneAction },
  });

  const mutate = useRunRulesMutation(props.id, {
    onSuccess: props.onSuccess,
  });

  const actions = form.watch([
    'add_to_annotation_queue_id',
    'add_to_dataset_id',
    'webhooks',
    'add_to_dataset_prefer_correction',
    'extend_only',
  ] as const);

  const [filterToUse, setFilters] = useTabAwareFilters({
    runTableFilters: props.runTableFilters,
    setRunTableFilters: props.setRunTableFilters,
    existingRuleFilters: props.initialData
      ? {
          filter: props.initialData?.filter ?? DEFAULT_FILTER_STRING,
          traceFilter: props.initialData?.trace_filter,
          treeFilter: props.initialData?.tree_filter,
        }
      : undefined,
  });

  const onSubmit = form.handleSubmit((json) => {
    const data = {
      ...json,
      session_id: props.sessionId,
      filter: filterToUse.filter ?? DEFAULT_FILTER_STRING,
      trace_filter: filterToUse.traceFilter,
      tree_filter: filterToUse.treeFilter,
    };
    if (!applyToPastRuns && !props.initialData?.backfill_from) {
      delete data.backfill_from;
    }
    mutate.trigger({ json: data });
  });

  return (
    <div className="flex grow flex-col gap-4">
      <ExpandableErrorAlert error={mutate.error} />
      <FormControl
        error={!!form.formState.errors.display_name}
        className="flex flex-col gap-2"
      >
        <Text variant="md" weight="medium">
          Name
        </Text>
        <MuiInput
          type="text"
          className="rounded-md border border-secondary bg-transparent px-2 py-[7px] outline-none"
          {...form.register('display_name', { required: 'Name is required' })}
        />
        <FormHelperText className="empty:hidden">
          {form.formState.errors.display_name?.message}
        </FormHelperText>
      </FormControl>

      <div className="flex flex-wrap gap-5">
        <FormControl>
          <Text variant="md" weight="medium">
            Run Filters
          </Text>
          <div className="mt-2">
            <RulesFilterInput
              sessionId={props.sessionId}
              value={filterToUse}
              onChange={setFilters}
            />
          </div>
        </FormControl>

        <FormControl className="flex flex-col gap-2">
          <Text variant="md" weight="medium">
            Sampling Rate
          </Text>
          <Controller
            name="sampling_rate"
            control={form.control}
            render={({ field }) => (
              <RulesSamplingRateSlider
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </FormControl>
        {!props.initialData && (
          <div className="flex flex-col gap-2">
            <Text variant="md" weight="medium" className="whitespace-nowrap">
              Apply to past runs
            </Text>
            <Switch
              size="sm"
              checked={applyToPastRuns}
              onChange={(event) => setApplyToPastRuns(event.target.checked)}
              sx={{
                alignSelf: 'flex-start',
                marginY: 'auto',
                paddingBottom: '8px',
              }}
            />
          </div>
        )}
        {(applyToPastRuns ||
          (props.initialData && props.initialData.backfill_from)) && (
          <FormControl
            error={!!form.formState.errors.backfill_from}
            sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <BackfillFromFormLabel disabled={!!props.initialData} />
            <Controller
              name="backfill_from"
              control={form.control}
              rules={{
                validate: validateBackfillDate,
              }}
              render={({ field, fieldState }) => (
                <BackfillDateInput
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!!props.initialData}
                  error={fieldState.error}
                />
              )}
            />
          </FormControl>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Text variant="md" weight="medium">
          Action
        </Text>
        <RulesActionInput
          value={actions}
          onChange={(change) => {
            if (change) {
              form.setValue(change.key, change.value);
              // Clear validation error when an action is selected
              form.clearErrors('add_to_annotation_queue_id');
            } else {
              form.setValue('add_to_annotation_queue_id', undefined);
              form.setValue('add_to_dataset_id', undefined);
              form.setValue('code_evaluators', undefined);
              form.setValue('evaluators', undefined);
              form.setValue('webhooks', undefined);
              form.setValue('extend_only', undefined);
            }
          }}
          sessionId={props.sessionId}
        />
        {form.formState.errors.add_to_annotation_queue_id && (
          <Text className="text-error">
            {form.formState.errors.add_to_annotation_queue_id.message}
          </Text>
        )}
      </div>

      <div className="grow" />

      <div className="flex w-full justify-between gap-2">
        {props.id && (
          <div className="flex flex-row items-center gap-2">
            <Text variant="md" weight="medium">
              Rule enabled
            </Text>
            <Controller
              name="is_enabled"
              control={form.control}
              render={({ field }) => (
                <Switch
                  size="md"
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        )}
        <div className="flex flex-row gap-2">
          <Button variant="outlined" onClick={props.onClose} size="md">
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={onSubmit}
            disabled={mutate.isMutating}
            loading={mutate.isMutating}
            size="md"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

type SelectedType =
  | 'add_to_annotation_queue_id'
  | 'add_to_dataset_id'
  | 'webhooks'
  | 'add_to_dataset_prefer_correction'
  | 'extend_only'
  | null;

// Define a type for the rule action change
type RuleActionChange =
  | { key: 'add_to_annotation_queue_id'; value: string }
  | { key: 'add_to_dataset_id'; value: string }
  | {
      key: 'webhooks';
      value: Exclude<RuleMutateBody['webhooks'], undefined | null>;
    }
  | { key: 'add_to_dataset_prefer_correction'; value: boolean }
  | { key: 'extend_only'; value: boolean }
  | null;

function RulesActionInput(props: {
  value: [
    RuleMutateBody['add_to_annotation_queue_id'],
    RuleMutateBody['add_to_dataset_id'],
    RuleMutateBody['webhooks'],
    RuleMutateBody['add_to_dataset_prefer_correction'],
    RuleMutateBody['extend_only']
  ];
  onChange: (change: RuleActionChange) => void;
  sessionId?: string;
}) {
  const [
    annotationQueueIdValue,
    datasetIdValue,
    webhooksValue,
    addToDatasetPreferCorrectValue,
    extendTracesValue,
  ] = props.value ?? [];

  const [selected, setSelected] = useState<SelectedType>(() => {
    if (annotationQueueIdValue) return 'add_to_annotation_queue_id';
    if (datasetIdValue) return 'add_to_dataset_id';
    if (webhooksValue && webhooksValue.length > 0) return 'webhooks';
    if (extendTracesValue) return 'extend_only';
    return null;
  });
  const showTTLUI = useInstanceFlagOrDefault(InstanceFlags.show_ttl_ui);

  const handleSelected = (value: typeof selected) => {
    if (selected !== value) {
      setSelected(value);
      props.onChange(null);
    }
  };

  const [isRulesWebhookPaneOpen, setIsRulesWebhookPaneOpen] = useState(false);
  const [webhookToEditIndex, setWebhookToEditIndex] = useState<
    number | undefined
  >(undefined);

  return (
    <div className="flex flex-col gap-3">
      <RulesActionSwitch
        active={selected === 'add_to_annotation_queue_id'}
        label="Add to annotation queue"
        onClick={() => handleSelected('add_to_annotation_queue_id')}
        docsLink="https://docs.langchain.com/langsmith/annotation-queues"
      >
        <RulesAnnotationQueueInput
          value={annotationQueueIdValue ?? undefined}
          onChange={(value) => {
            props.onChange({ key: 'add_to_annotation_queue_id', value });
          }}
        />
      </RulesActionSwitch>

      <RulesActionSwitch
        active={selected === 'add_to_dataset_id'}
        label="Add to dataset"
        onClick={() => handleSelected('add_to_dataset_id')}
        docsLink="https://docs.langchain.com/langsmith/manage-datasets-in-application#manually-from-a-tracing-project"
      >
        <div className="flex flex-col gap-3">
          <RulesDatasetInput
            value={datasetIdValue ?? undefined}
            onChange={(value) => {
              if (value) {
                props.onChange({ key: 'add_to_dataset_id', value });
              }
            }}
          />
          <div className="flex flex-row items-center gap-2">
            <Checkbox
              checked={addToDatasetPreferCorrectValue ?? false}
              onChange={(evt) =>
                props.onChange({
                  key: 'add_to_dataset_prefer_correction',
                  value: evt.target.checked,
                })
              }
            />
            <Text variant="sm">Use Corrections</Text>
            <Tooltip
              title={
                <span>
                  Use feedback correction field as the dataset example outputs.
                  <br />
                  For runs without feedback fallback to run outputs.
                </span>
              }
            >
              <div>
                <InfoCircleIcon className="h-4 w-4 text-tertiary" />
              </div>
            </Tooltip>
          </div>
        </div>
      </RulesActionSwitch>

      <RulesActionSwitch
        active={selected === 'webhooks'}
        label={
          <div className="flex flex-row items-center gap-2">
            <Text variant="md">Trigger webhooks</Text>
            {(webhooksValue?.length ?? 0) > 0 && (
              <Text variant="sm">
                ({webhooksValue?.length}{' '}
                {pluralize('endpoint', webhooksValue?.length ?? 0)})
              </Text>
            )}
          </div>
        }
        onClick={() => handleSelected('webhooks')}
        docsLink="https://docs.langchain.com/langsmith/webhooks"
      >
        <div className="flex flex-col gap-3">
          {(webhooksValue?.length ?? 0) > 0 ? (
            <div className="flex flex-col gap-2">
              {webhooksValue?.map((webhook, idx) => (
                <Chip
                  key={idx}
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  sx={{ borderRadius: '4px', fontWeight: 'normal' }}
                  onClick={() => {
                    setWebhookToEditIndex(idx);
                    setIsRulesWebhookPaneOpen(true);
                  }}
                >
                  {webhook.url}
                </Chip>
              ))}
            </div>
          ) : (
            <Button
              color="secondary"
              onClick={() => setIsRulesWebhookPaneOpen(true)}
              className="w-fit"
              leftDecorator={PlusIcon}
            >
              Endpoint
            </Button>
          )}
        </div>
        <RulesWebhookPane
          value={webhooksValue ?? undefined}
          onChange={(value) => {
            props.onChange({ key: 'webhooks', value });
          }}
          open={isRulesWebhookPaneOpen}
          setOpen={(open) => {
            setIsRulesWebhookPaneOpen(open);
            if (!open) {
              setWebhookToEditIndex(undefined);
            }
          }}
          webhookToEditIndex={webhookToEditIndex}
        />
      </RulesActionSwitch>

      {showTTLUI && (
        <RulesActionSwitch
          active={selected === 'extend_only'}
          label="Extend Data Retention"
          onClick={() => {
            handleSelected('extend_only');
            props.onChange({ key: 'extend_only', value: true });
          }}
          docsLink="https://docs.langchain.com/langsmith/administration-overview#data-retention"
        />
      )}
    </div>
  );
}

export function RulesSamplingRateSlider(props: {
  value: number | undefined;
  onChange: (value: number) => void;
}) {
  const [localValue, setLocalValue] = useState<string | undefined>(
    props.value?.toString()
  );

  // Helper function to check if input is a valid partial or complete sampling rate
  const isValidSamplingRateInput = (inputStr: string): boolean => {
    // Allow empty string (user cleared the field)
    if (!inputStr) return true;

    // Allow single decimal point
    if (inputStr === '.') return true;

    // Allow leading zero with decimal point
    if (inputStr === '0.') return true;

    // Allow just '1' or '1.' as intermediate states
    if (inputStr === '1' || inputStr === '1.') return true;

    // Check for valid number format with decimal
    const numValue = parseFloat(inputStr);
    if (isNaN(numValue)) return false;

    // Must be between 0 and 1 (inclusive)
    if (numValue < 0 || numValue > 1) return false;

    // Check decimal places - allow up to 4 decimal places for precision
    if (inputStr.includes('.')) {
      const decimalPart = inputStr.split('.')[1];
      if (decimalPart && decimalPart.length > 4) return false;
    }

    return true;
  };

  const handleBlur = useCallback(() => {
    // Only call props.onChange when user finishes typing (on blur)
    if (localValue && !Number.isNaN(Number(localValue))) {
      const num = Number(localValue);
      if (num >= 0 && num <= 1) {
        props.onChange(num);
      }
    } else if (!localValue || localValue === '') {
      // If field is empty, set to default value of 1
      setLocalValue('1');
      props.onChange(1);
    }
  }, [localValue, props.onChange]);

  return (
    <div className="grid grid-cols-[1fr,auto] gap-4">
      <Slider
        min={0}
        max={1}
        value={localValue ? Number(localValue) : 1}
        step={0.01}
        onChange={(_, value) => {
          if (!Array.isArray(value)) {
            setLocalValue(value.toString());
            props.onChange(value);
          }
        }}
        sx={{ py: 0, minWidth: '60px' }}
      />
      <div className="max-w-16">
        <Input
          size="sm"
          value={localValue ?? ''}
          onChange={(value) => {
            if (isValidSamplingRateInput(value)) {
              setLocalValue(value);
            }
          }}
          onBlur={handleBlur}
        />
      </div>
    </div>
  );
}
